import React from "react";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: any;
  iconColor: string;
  iconBgColor: string;
  className?: string;
}

interface MetricCardGridProps {
  children: React.ReactNode;
  cardCount?: number;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBgColor,
  className = "",
}) => {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 ${className}`}>
      <div className="flex items-center gap-4">
        <div
          className={`w-12 h-12 rounded-lg ${iconBgColor} flex items-center justify-center`}
        >
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 mb-1 truncate">{title}</p>
          <p className="text-2xl font-bold text-gray-900 truncate">{value || "0"}</p>
        </div>
      </div>
    </div>
  );
};

export const MetricCardGrid: React.FC<MetricCardGridProps> = ({
  children,
  cardCount
}) => {
  // Count children if cardCount is not provided
  const count = cardCount || React.Children.count(children);

  // Determine responsive grid classes based on card count
  const getGridClasses = (cardCount: number): string => {
    const baseClasses = "grid gap-4 sm:gap-6 mb-8";

    switch (cardCount) {
      case 1:
        return `${baseClasses} grid-cols-1 max-w-md mx-auto`;
      case 2:
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto`;
      case 3:
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`;
      case 4:
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-4`;
      case 5:
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5`;
      case 6:
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6`;
      default:
        // For 7+ cards, use a flexible grid that wraps nicely
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5`;
    }
  };

  return (
    <div className={getGridClasses(count)}>
      {children}
    </div>
  );
};
